import { useCallback, useState } from 'react';
import { Alert, Badge, Button, Card, DiscordIcon, EmptyState } from '@playerbase/ui';
import type { DiscordConnection } from '@playerbase/types';

import {
  disconnectDiscord,
  startDiscordConnect,
  type StartDiscordConnectError,
} from '../../../games/discord-data.js';
import { useDiscordRecheckOnMount } from '../../../games/use-discord-recheck-on-mount.js';
import { DisconnectConfirmDialog } from './disconnect-confirm-dialog.js';

// ---------------------------------------------------------------------------
// ConnectionCard - the single render for all three states the story
// describes:
//   - No connection         -> CTA: "Connect a Discord server"
//   - Connected              -> guild card, Reconnect + Disconnect actions
//   - Needs attention        -> warning Alert + prominent Reconnect
//
// The card is self-contained: it owns the start/disconnect/recheck calls,
// and lifts a refresh callback so the parent page can reload the connection
// row after the bot updates it.
// ---------------------------------------------------------------------------

type ConnectionCardProps = {
  studioId: string;
  gameId: string;
  connection: DiscordConnection | null;
  onChanged: () => Promise<void> | void;
};

function startErrorMessage(code: StartDiscordConnectError): string {
  switch (code) {
    case 'not_authenticated':
      return 'You need to be signed in to connect a Discord server. Try signing in again.';
    case 'not_a_member':
      return 'You are not a member of this studio.';
    case 'game_not_found':
      return 'This game no longer exists. Refresh the page and try again.';
    case 'game_studio_mismatch':
      return 'This game does not belong to the active studio.';
    case 'server_misconfigured':
      return 'Discord OAuth is not configured for this environment. Tell whoever set up the project to fill in DISCORD_CLIENT_ID and DISCORD_CLIENT_SECRET.';
    case 'persist_failed':
      return 'Could not save the connection request. Try again in a moment.';
    case 'unknown':
    default:
      return 'Something went wrong starting the Discord connection. Try again.';
  }
}

export function ConnectionCard({
  studioId,
  gameId,
  connection,
  onChanged,
}: ConnectionCardProps): JSX.Element {
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [showDisconnect, setShowDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  useDiscordRecheckOnMount(connection?.id ?? null, connection?.status ?? null);

  const handleConnect = useCallback(async (): Promise<void> => {
    setStarting(true);
    setStartError(null);
    try {
      const result = await startDiscordConnect({ studioId, gameId });
      if (!result.ok) {
        setStartError(startErrorMessage(result.code));
        return;
      }
      // Full-page redirect (rather than a popup window) - popup blockers and
      // sandboxed iframes are too inconsistent for a multi-step OAuth
      // consent flow. Discord redirects back to /oauth/discord-callback
      // which finishes the round-trip and navigates back to this page.
      window.location.href = result.url;
    } finally {
      setStarting(false);
    }
  }, [studioId, gameId]);

  const handleDisconnect = useCallback(async (): Promise<void> => {
    if (connection === null) return;
    setDisconnecting(true);
    setDisconnectError(null);
    try {
      const result = await disconnectDiscord(connection.id);
      if (!result.ok) {
        setDisconnectError('Could not disconnect right now. Try again.');
        return;
      }
      setShowDisconnect(false);
      await onChanged();
    } finally {
      setDisconnecting(false);
    }
  }, [connection, onChanged]);

  // ---- No connection ------------------------------------------------------

  if (connection === null) {
    return (
      <>
        <EmptyState
          eyebrow="Discord"
          title="Connect a Discord server"
          description="Link this game to a Discord server so the bot can join it. Once connected, you can pick which channels feed into the Feedback Pool."
          cta={
            <Button variant="discord" size="md" onClick={() => void handleConnect()} disabled={starting}>
              <DiscordIcon className="h-4 w-auto" />
              {starting ? 'Opening Discord…' : 'Connect a Discord server'}
            </Button>
          }
          footnote="You will be redirected to Discord to choose the server. You need Manage Server permission on the target server."
        />
        {startError !== null && (
          <Alert tone="danger" className="mt-4">
            {startError}
          </Alert>
        )}
      </>
    );
  }

  // ---- Connected / Needs attention ---------------------------------------

  const isNeedsAttention = connection.status === 'needs_attention';
  const isPending = connection.status === 'pending';

  return (
    <>
      <Card>
        <Card.Header>
          <div className="flex items-start gap-4">
            {connection.guildIconUrl !== null ? (
              <img
                src={connection.guildIconUrl}
                alt=""
                className="h-12 w-12 shrink-0 rounded-md border border-border"
              />
            ) : (
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-accent-bg text-text">
                <DiscordIcon className="h-6 w-auto" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <Card.Title className="truncate">{connection.guildName}</Card.Title>
              <Card.Description>
                {isNeedsAttention
                  ? 'Needs attention - the bot has lost access to this server.'
                  : isPending
                    ? 'Waiting for the bot to confirm the install. This usually takes a few seconds.'
                    : 'Connected. The bot is in your server and ready to watch channels.'}
              </Card.Description>
            </div>
            <Badge tone={isNeedsAttention ? 'warning' : isPending ? 'info' : 'success'}>
              {isNeedsAttention ? 'Needs attention' : isPending ? 'Pending' : 'Connected'}
            </Badge>
          </div>
        </Card.Header>

        {isNeedsAttention && (
          <Card.Content>
            <Alert tone="warning">
              The bot was kicked, lost a required permission, or the OAuth grant
              was revoked. Click <strong>Reconnect</strong> to re-run the install
              against the same server.
            </Alert>
          </Card.Content>
        )}

        <Card.Footer>
          <Button
            variant={isNeedsAttention ? 'primary' : 'ghost'}
            size="md"
            onClick={() => void handleConnect()}
            disabled={starting}
          >
            {starting ? 'Opening Discord…' : 'Reconnect'}
          </Button>
          <Button
            variant="ghost"
            size="md"
            onClick={() => setShowDisconnect(true)}
            disabled={starting}
          >
            Disconnect
          </Button>
        </Card.Footer>
      </Card>

      {startError !== null && (
        <Alert tone="danger" className="mt-4">
          {startError}
        </Alert>
      )}

      <DisconnectConfirmDialog
        guildName={connection.guildName}
        open={showDisconnect}
        busy={disconnecting}
        errorMessage={disconnectError}
        onConfirm={() => void handleDisconnect()}
        onCancel={() => {
          if (disconnecting) return;
          setShowDisconnect(false);
          setDisconnectError(null);
        }}
      />
    </>
  );
}
